
import { Component, Vue } from 'vue-property-decorator';
import { message } from 'ant-design-vue';
import API from '@/services/rest-api';
import { CleanupReport, CleanupType } from '@/interfaces/cleanup';
import { UnusedVMImagesReport } from '@/interfaces/vm-image';

const options = [
  { type: CleanupType.VM_IMAGES, label: 'VM images' },
  { type: CleanupType.DOCKER_IMAGES, label: 'Docker images' },
  { type: CleanupType.DISKS, label: 'disks' },
  { type: CleanupType.MACHINE_IMAGES, label: 'machine images' },
  { type: CleanupType.STATIC_IPS, label: 'static IPs' }
];

const columns = [
  { title: 'Name', dataIndex: 'name', key: 'name' },
  { title: 'Size', dataIndex: 'sizeGb', key: 'sizeGb', width: 100, scopedSlots: { customRender: 'sizeGb' } },
  {
    title: 'Created',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 110,
    scopedSlots: { customRender: 'createdAt' }
  },
  { title: 'Details', dataIndex: 'details', key: 'details', width: 220 }
];

const toCleanupReport = (report: UnusedVMImagesReport): CleanupReport => ({
  type: CleanupType.VM_IMAGES,
  projectId: report.projectId,
  unused: report.unused.map((image) => ({
    name: image.name,
    sizeGb: image.sizeGb,
    createdAt: image.createdAt,
    details: ''
  })),
  unusedSizeGb: report.unusedSizeGb,
  keptCount: report.used.length,
  warnings: report.envErrors,
  note: 'Not in the analytic-launcher .env of dev or prod and not used by a disk. Drone, pix4d and tiling images are always kept.',
  isDeleting: false
});

@Component
export default class UnusedResources extends Vue {
  options = options;
  columns = columns;
  type: CleanupType = null;
  report: CleanupReport = null;
  selectedRowKeys: string[] = [];
  isVisible = false;
  isLoading = false;
  isDeleting = false;

  get label(): string {
    return options.find((option) => option.type === this.type)?.label || '';
  }

  get canDelete(): boolean {
    return (
      this.selectedRowKeys.length > 0 && !this.report?.warnings.length && !this.report?.isDeleting && !this.isDeleting
    );
  }

  onSelectionChange(selectedRowKeys: string[]): void {
    this.selectedRowKeys = selectedRowKeys;
  }

  async find(type: CleanupType): Promise<void> {
    this.type = type;
    this.isLoading = true;
    try {
      const report =
        type === CleanupType.VM_IMAGES
          ? toCleanupReport(await API.findUnusedVMImages())
          : await API.findUnusedResources(type);
      this.report = report;
      this.selectedRowKeys = report.unused.map((item) => item.name);
      this.isVisible = true;
    } finally {
      this.isLoading = false;
    }
  }

  async deleteSelected(): Promise<void> {
    this.isDeleting = true;
    try {
      if (this.type === CleanupType.VM_IMAGES) {
        const result = await API.deleteUnusedVMImages(this.selectedRowKeys);
        const notDeleted = [...result.failed, ...result.skipped];
        const summary = `Deleted ${result.deleted.length} VM images`;
        if (notDeleted.length) {
          message.warning(`${summary}. Not deleted: ${notDeleted.join(', ')}`, 10);
        } else {
          message.success(summary);
        }
      } else {
        const started = await API.deleteUnusedResources(this.type, this.selectedRowKeys);
        const skipped = started.skipped.length ? ` Skipped, in use again: ${started.skipped.join(', ')}.` : '';
        message.info(`Deleting ${started.started} ${this.label} in the background.${skipped}`, 10);
      }
    } finally {
      this.isDeleting = false;
    }
    await this.find(this.type);
  }
}
